import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

interface ExpandableTextProps {
  text: string;
  className?: string;
}

export const ExpandableText = ({
  text,
  className,
}: ExpandableTextProps): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (textRef.current) {
      // Check if text is overflowing its container
      setIsOverflowing(
        textRef.current.scrollHeight > textRef.current.clientHeight,
      );
    }
  }, [text]);

  useEffect(() => {
    const handleResize = (): void => {
      const width = window.innerWidth;
      const { outerWidth } = window;

      if (width < outerWidth / 2 && textRef.current) {
        setIsOverflowing(
          textRef.current.scrollHeight > textRef.current.clientHeight,
        );
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return (): void => window.removeEventListener("resize", handleResize);
  }, [textRef]);

  const toggleExpand = (): void => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className='relative w-full'>
      <p
        ref={textRef}
        className={cn(
          `text-dark-500 font-medium text-sm max-w-full transition-all duration-300 ease-in-out`,
          !isExpanded ? "line-clamp-2" : "",
          className,
        )}
      >
        {text}
      </p>
      {isOverflowing && (
        <button
          onClick={toggleExpand}
          className='text-xs font-medium mt-1 focus:outline-none text-primary underline'
        >
          {isExpanded ? "Show Less" : "Show More"}
        </button>
      )}
    </div>
  );
};
